import parsePhoneNumber, { getCountries, getCountryCallingCode } from 'libphonenumber-js';

/**
 * Manually extracts user digits from a phone number when libphonenumber-js fails
 * Useful for edge cases like single digit inputs that can't be parsed properly
 *
 * @param phoneNumber - The full phone number string (e.g., "+11")
 * @param country - The country object with dialCode (e.g., {dialCode: "1"}) - optional
 * @returns The user digits string (e.g., "1") or empty string if invalid
 */
export const extractUserDigits = (phoneNumber: string, country?: any): string => {
  if (!phoneNumber) return '';

  // If no country provided, return empty string for safety
  if (!country?.dialCode) return '';

  const countryCodeWithPlus = `+${country.dialCode}`;
  // Remove country code and any non-digit characters to get just user digits
  return phoneNumber.replace(countryCodeWithPlus, '').replace(/[^\d]/g, '');
};

/**
 * Extracts the national number (without country code) from a phone number string
 * Uses libphonenumber-js to parse and extract the national number safely
 * Falls back to manual extraction for edge cases like incomplete inputs
 *
 * @param phoneNumber - The full phone number string (e.g., "+12345678901" or "+12")
 * @param country - The country object with dialCode (optional, used for fallback)
 * @returns The national number string (e.g., "2345678901" or "2") or empty string if invalid
 */
export const extractNationalNumber = (phoneNumber: string, country?: any): string => {
  if (!phoneNumber || phoneNumber.trim() === '') {
    return '';
  }

  // Try to parse with libphonenumber-js
  try {
    const parsed = parsePhoneNumber(phoneNumber);
    // If we got a valid national number, return it
    if (parsed?.nationalNumber) {
      return parsed.nationalNumber;
    }
  } catch (error: any) {
    // Parse failed, continue to fallback
  }

  // Manual extraction for incomplete numbers
  // If country is provided, use it directly
  if (country?.dialCode) {
    return extractUserDigits(phoneNumber, country);
  }

  // Try to detect country code and extract national number
  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');

  if (digitsOnly.length <= 1) {
    return '';
  }

  // Try to match against known country calling codes
  // Check 1-digit, 2-digit, and 3-digit country codes
  for (const countryCode of getCountries()) {
    try {
      const callingCode = getCountryCallingCode(countryCode);
      if (digitsOnly.startsWith(callingCode)) {
        const nationalNumber = digitsOnly.substring(callingCode.length);
        if (nationalNumber) {
          return nationalNumber;
        }
      }
    } catch (e) {
      // Skip invalid country codes
    }
  }

  return '';
};

/**
 * Compares two phone values the way the API sees them, ignoring formatting.
 *
 * Needed because `usePhoneInput` (react-international-phone) re-formats whatever value it is given
 * and fires `onChange` on mount when its formatted output differs from that value — '' turns into
 * '+1', and a stored '+15551234567' comes back spaced/bracketed. Neither is a user edit, so callers
 * use this to ignore updates that would leave the payload unchanged.
 *
 * @param phoneA - First phone number string
 * @param phoneB - Second phone number string
 * @param country - Optional country object with dialCode, used as a parsing fallback
 * @returns true when both values would be sent to the API as the same thing
 */
export const isSamePhoneNumberValue = (phoneA: string, phoneB: string, country?: any): boolean => {
  const isPhoneAEmpty = extractNationalNumber(phoneA, country) === '';
  const isPhoneBEmpty = extractNationalNumber(phoneB, country) === '';

  // Dial-code-only values ('', '+1', '+44') all mean "no number" — the payload sends null for each,
  // so they are equivalent to each other, but never to a value that has actual digits.
  if (isPhoneAEmpty || isPhoneBEmpty) return isPhoneAEmpty && isPhoneBEmpty;

  // Compare every digit, country code included, so switching country still counts as a change.
  return phoneA.replace(/\D/g, '') === phoneB.replace(/\D/g, '');
};
